import { ref, computed, onUnmounted } from "vue";
import { getSeasons, getOverview } from "@/services/dashboard/dashboard";
import { useDashboardCharts } from "@/composables/admin/dashboard/useDashboardCharts";

/**
 * Composable del dashboard de club.
 * Gestiona el estado reactivo, la obtención de temporadas y overview,
 * y la cancelación de peticiones con AbortController.
 *
 * @returns {{
 *     seasons: import('vue').Ref<Array>,
 *     selectedSeasonIds: import('vue').Ref<number[]>,
 *     overviewData: import('vue').Ref<object|null>,
 *     loadingSeasons: import('vue').Ref<boolean>,
 *     loadingOverview: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     loadedSeasonCache: import('vue').Ref<object>,
 *     currentSeasonData: import('vue').ComputedRef<object|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     aggregateKpis: import('vue').ComputedRef<object|null>,
 *     kpis: import('vue').ComputedRef<{ revenue: number, attendance: number, abonados: number, occupancy: number }|null>,

 *     meta: import('vue').ComputedRef<object|null>,
 *     fetchSeasons: () => Promise<void>,
 *     fetchOverview: (seasonIds: number[]) => Promise<void>,
 *     selectSeasons: (ids: number[]) => void,
 * }}
 */
export function useDashboard() {
    // ==================== Estado reactivo ====================
    const seasons = ref([]);
    const selectedSeasonIds = ref([]);
    const overviewData = ref(null);
    const loadingSeasons = ref(false);
    const loadingOverview = ref(false);
    const error = ref(null);
    const loadedSeasonCache = ref({});

    // AbortControllers para cancelación
    let seasonsAbortController = null;
    let overviewAbortController = null;

    // Limpieza al desmontar
    onUnmounted(() => {
        seasonsAbortController?.abort();
        overviewAbortController?.abort();
    });

    // ==================== Computed ====================

    /** Entrada completa de la primera temporada seleccionada (club + avg_occupancy_pct al mismo nivel) */
    const currentSeasonEntry = computed(() => {
        if (!overviewData.value || !selectedSeasonIds.value.length) return null;
        const firstId = selectedSeasonIds.value[0];
        return overviewData.value?.data?.per_season?.[firstId] || null;
    });

    /** Datos de club de la temporada seleccionada */
    const currentSeasonData = computed(() => {
        return currentSeasonEntry.value?.club || null;
    });

    /** Datos del meta (teatro_nombre, generated_at) */
    const meta = computed(() => {
        return overviewData.value?.meta || null;
    });

    /** Indica si hay más de una temporada seleccionada */
    const isMultiSeason = computed(() => selectedSeasonIds.value.length > 1);

    /** KPIs agregados cuando hay múltiples temporadas */
    const aggregateKpis = computed(() => {
        if (!isMultiSeason.value || !overviewData.value) return null;
        const perSeason = overviewData.value?.data?.per_season || {};
        const entries = Object.values(perSeason).filter(Boolean);
        if (entries.length === 0) return null;
        const count = entries.length;
        const totalRevenue = entries.reduce(
            (sum, e) => sum + (e.club?.total_revenue || 0),
            0,
        );
        const avgAttendance =
            entries.reduce((sum, e) => sum + (e.club?.attendance_avg || 0), 0) /
            count;
        const totalAbonados = entries.reduce(
            (sum, e) => sum + (e.club?.abonados_count || 0),
            0,
        );
        const avgOccupancy =
            entries.reduce((sum, e) => sum + Number(e.avg_occupancy_pct || 0), 0) /
            count;
        return {
            revenue: totalRevenue,
            attendance: avgAttendance,
            abonados: totalAbonados,
            occupancy: avgOccupancy,
        };
    });

    /** KPIs formateados para los cards */
    const kpis = computed(() => {
        // Siempre mostramos los datos de la primera temporada seleccionada.
        // La comparativa se ve en los charts y la tabla de comparación.
        const data = currentSeasonData.value;
        if (!data) return null;

        return {
            revenue: data.total_revenue ?? 0,
            attendance: data.attendance_avg ?? 0,
            abonados: data.abonados_count ?? 0,
            occupancy: Number(currentSeasonEntry.value?.avg_occupancy_pct ?? 0),
        };
    });

    // ==================== Chart Data (delegado) ====================
    const {
        revenueChartType,
        occupancyChartType,
        revenueChartData,
        occupancyChartData,
        comparisonTableData,
    } = useDashboardCharts(
        seasons,
        selectedSeasonIds,
        overviewData,
        loadedSeasonCache,
        isMultiSeason,
    );

    // ==================== Métodos ====================

    /**
     * Obtiene la lista de temporadas.
     * Auto-selecciona la primera temporada activa.
     */
    async function fetchSeasons() {
        // Cancelar petición previa
        seasonsAbortController?.abort();
        seasonsAbortController = new AbortController();

        loadingSeasons.value = true;
        error.value = null;

        try {
            const response = await getSeasons(seasonsAbortController.signal);
            seasons.value = response.data?.data || [];

            // Auto-seleccionar primera temporada activa
            const activeSeason = seasons.value.find(s => s.activa);
            if (activeSeason) {
                selectedSeasonIds.value = [activeSeason.id];
                await fetchOverview([activeSeason.id]);
            }
        } catch (err) {
            if (err.name === "CanceledError" || err.name === "AbortError") return;
            error.value = err.message || "Error al cargar las temporadas";
            if (process.env.NODE_ENV !== "production") {
                console.error("Error fetching seasons:", err);
            }
        } finally {
            loadingSeasons.value = false;
        }
    }

    /**
     * Obtiene el overview para una o varias temporadas.
     * Almacena en caché los datos cargados para la comparativa.
     *
     * @param {number[]} seasonIds
     */
    async function fetchOverview(seasonIds) {
        // Cancelar petición previa de overview
        overviewAbortController?.abort();
        overviewAbortController = new AbortController();

        loadingOverview.value = true;
        error.value = null;

        // Ocultar datos previos mientras carga
        overviewData.value = null;

        try {
            const response = await getOverview(
                seasonIds,
                overviewAbortController.signal,
            );
            overviewData.value = response.data;

            // Almacenar en caché para la comparativa (todas las temporadas devueltas)
            const perSeason = response.data?.data?.per_season || {};
            const cache = { ...loadedSeasonCache.value };
            for (const [sid, entry] of Object.entries(perSeason)) {
                if (entry?.club) {
                    cache[sid] = {
                        ...entry.club,
                        avg_occupancy_pct: Number(entry.avg_occupancy_pct ?? 0),
                    };
                }
            }
            loadedSeasonCache.value = cache; // Triggers reactivity
        } catch (err) {
            if (err.name === "CanceledError" || err.name === "AbortError") return;
            error.value = err.message || "Error al cargar los datos del dashboard";
            if (process.env.NODE_ENV !== "production") {
                console.error("Error fetching overview:", err);
            }
        } finally {
            loadingOverview.value = false;
        }
    }

    /**
     * Selecciona una o varias temporadas desde el multiselect
     * y dispara la carga de su overview.
     * Cancela cualquier petición previa en curso.
     *
     * @param {number[]} ids
     */
    function selectSeasons(ids) {
        selectedSeasonIds.value = ids;
        if (ids.length > 0) {
            fetchOverview(ids);
        }
    }

    return {
        // Estado
        seasons,
        selectedSeasonIds,
        overviewData,
        loadingSeasons,
        loadingOverview,
        error,
        loadedSeasonCache,

        // Computed
        currentSeasonData,
        isMultiSeason,
        aggregateKpis,
        kpis,
        revenueChartType,
        occupancyChartType,
        revenueChartData,
        occupancyChartData,
        comparisonTableData,
        meta,

        // Métodos
        fetchSeasons,
        fetchOverview,
        selectSeasons,
    };
}
